import React, { useState } from "react";
import Icon from "@amzn/meridian/icon";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import infoTokens from "@amzn/meridian-tokens/base/icon/info";
import Input from "@amzn/meridian/input";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Select, { SelectOption } from "@amzn/meridian/select";
import Tooltip from "@amzn/meridian/tooltip";
import Button from "@amzn/meridian/button";
import { ProcessPathOverride } from '../../../../store/slices/volumesAndWipSlice';
import { TOOLTIP_EXPECTED_VOLUME } from '../../../../i18n/tooltips';
import '../../index.scss';

interface ProcessPathVolumeRowProps {
    processPathDisplayName: string;
    processPathName: string;
    targetVolume: number;
    wipVolume?: number;
    readOnly?: boolean;
    processPathOverride: ProcessPathOverride;
    updateProcessPathOverride: (processPathName: string, field: 'override' | 'wipOverride' | 'overrideReason' | 'overrideComment' | 'wipOverrideReason' | 'wipOverrideComment', value: number | string | undefined) => void;
}

interface OverrideReasonsProps {
    processPathName: string;
    processPathOverride: ProcessPathOverride;
    overrideReasonsList: string[];
    readOnly?: boolean;
    updateProcessPathOverride: (processPathName: string, field: 'override' | 'wipOverride' | 'overrideReason' | 'overrideComment' | 'wipOverrideReason' | 'wipOverrideComment', value: number | string | undefined) => void;
    overrideType: 'volume' | 'wip';
    overrideValue?: number;
}

const OverrideReasons: React.FC<OverrideReasonsProps> = ({ processPathName, processPathOverride, updateProcessPathOverride, overrideReasonsList, readOnly = false, overrideType, overrideValue }) => {
    const reasonField = overrideType === 'volume' ? 'overrideReason' : 'wipOverrideReason';
    const commentField = overrideType === 'volume' ? 'overrideComment' : 'wipOverrideComment';

    const reasonValue = overrideType === 'volume' ? processPathOverride.overrideReason : processPathOverride.wipOverrideReason;
    const commentValue = overrideType === 'volume' ? processPathOverride.overrideComment : processPathOverride.wipOverrideComment;

    const hasOverride = overrideValue !== undefined && overrideValue !== null;
    const hasError = hasOverride && !reasonValue;

    const onReasonChange = (value: string) => {
        updateProcessPathOverride(processPathName, reasonField, value);
    };

    const onOverrideCommentChange = (value: string) => {
        updateProcessPathOverride(processPathName, commentField, value);
    };

    return (
        <Row spacing="300" alignmentVertical="top" wrap="down">
            <Column spacing="100" minWidth={160}>
                <Text type="b200" color="secondary">Override Reason</Text>
                {readOnly ? (
                    <Text type="b300">{reasonValue || '—'}</Text>
                ) : (
                <Select
                    value={reasonValue || ''}
                    onChange={onReasonChange}
                    width={160}
                    size="small"
                    error={hasError}
                    errorMessage={hasError ? "Override Reason is mandatory" : undefined}
                >
                    <SelectOption value="" label="Select Reason" />
                    {overrideReasonsList.map((reasonOption) => (
                        <SelectOption
                            key={reasonOption}
                            value={reasonOption}
                            label={reasonOption}
                        />
                    ))}
                </Select>
                )}
            </Column>
            <Column spacing="100" minWidth={160}>
                <Text type="b200" color="secondary">Comment</Text>
                {readOnly ? (
                    <Text type="b300">{commentValue || '—'}</Text>
                ) : (
                <Input
                    value={commentValue || ''}
                    onChange={onOverrideCommentChange}
                    placeholder="Comment"
                    width={160}
                    size="small"
                />
                )}
            </Column>
        </Row>
    );
}

const ProcessPathVolumeRow: React.FC<ProcessPathVolumeRowProps> = ({
    processPathDisplayName,
    processPathName,
    targetVolume,
    wipVolume,
    readOnly = false,
    processPathOverride,
    updateProcessPathOverride
}) => {
    const [expanded, setExpanded] = useState(true);

    const toggleExpanded = () => { setExpanded(!expanded); }

    const onVolumeOverrideChange = (value: string) => {
        updateProcessPathOverride(processPathName, 'override', value ? parseFloat(value) : undefined);
    };

    const onWipOverrideChange = (value: string) => {
        updateProcessPathOverride(processPathName, 'wipOverride', value ? parseFloat(value) : undefined);
    };

    const volumeOverrideReasonsList = ["Backlog", "Hours Miss", "Other"]
    const wipOverrideReasonsList = ["Untracked piles", "Other"]

    return (
        <div className="category-row">
            {/* Compact header: process path name + summary on one line */}
            <div className="category-header" onClick={toggleExpanded}>
                <Icon tokens={expanded ? chevronDownSmallTokens : chevronRightSmallTokens} color="primary" />
                <Text type="b400" className="category-name">{processPathDisplayName}</Text>
                {!expanded && (
                    <span className="category-summary">
                        <Text type="b200" color="secondary">
                            Vol: {targetVolume.toLocaleString()}
                            {processPathOverride.override && ` → ${processPathOverride.override.toLocaleString()}`}
                            {wipVolume !== undefined && wipVolume > 0 && ` | Buffer: ${wipVolume.toLocaleString()}`}
                            {processPathOverride.wipOverride && ` → ${processPathOverride.wipOverride.toLocaleString()}`}
                        </Text>
                    </span>
                )}
            </div>

            {expanded && (
                <div className="category-details">
                    {/* Volume Section */}
                    <div className="detail-block">
                        <Row spacing="500" alignmentVertical="top" wrap="down">
                            <Column spacing="100" minWidth={120}>
                                {processPathDisplayName === "Primary Grading" ? (
                                    <Text type="b200" color="secondary">ALPS Target</Text>
                                ) : (
                                    <Row spacing="100" alignmentVertical="center">
                                        <Text type="b200" color="secondary">Expected Volume</Text>
                                        <Tooltip title={TOOLTIP_EXPECTED_VOLUME} position="top" maxWidth={280}>
                                            <Button type="icon" size="small" label="Info: Expected Volume">
                                                <Icon tokens={infoTokens} />
                                            </Button>
                                        </Tooltip>
                                    </Row>
                                )}
                                <Text type="b400">{targetVolume.toLocaleString()} Units</Text>
                            </Column>
                            <Column spacing="100" minWidth={120}>
                                <Text type="b200" color="secondary">Override</Text>
                                {readOnly ? (
                                    <Text type="b300">{processPathOverride.override != null ? `${processPathOverride.override.toLocaleString()} Units` : '—'}</Text>
                                ) : (
                                <Row spacing="100" alignmentVertical="center">
                                    <Input
                                        value={processPathOverride.override?.toString() || ''}
                                        onChange={onVolumeOverrideChange}
                                        width={100}
                                        type="number"
                                        size="small"
                                    />
                                    <Text type="b200" color="secondary">Units</Text>
                                </Row>
                                )}
                            </Column>
                        </Row>
                        {processPathOverride.override !== undefined && processPathOverride.override !== null && (
                            <OverrideReasons 
                                processPathName={processPathName}
                                processPathOverride={processPathOverride}
                                updateProcessPathOverride={updateProcessPathOverride}
                                overrideReasonsList={volumeOverrideReasonsList}
                                readOnly={readOnly}
                                overrideType="volume"
                                overrideValue={processPathOverride.override}
                            />
                        )}
                    </div>

                    {/* Buffer Section — show when wipVolume is provided (even if 0, to allow override) */}
                    {wipVolume !== undefined && (
                        <div className="detail-block">
                            <Row spacing="500" alignmentVertical="top" wrap="down">
                                <Column spacing="100" minWidth={120}>
                                    <Text type="b200" color="secondary">Buffer</Text>
                                    {wipVolume > 0 && <Text type="b400">{wipVolume.toLocaleString()} Units</Text>}
                                </Column>
                                <Column spacing="100" minWidth={120}>
                                    <Text type="b200" color="secondary">Override</Text>
                                    {readOnly ? (
                                        <Text type="b300">{processPathOverride.wipOverride != null ? `${processPathOverride.wipOverride.toLocaleString()} Units` : '—'}</Text>
                                    ) : (
                                    <Row spacing="100" alignmentVertical="center">
                                        <Input
                                            value={processPathOverride.wipOverride?.toString() || ''}
                                            onChange={onWipOverrideChange}
                                            width={100}
                                            type="number"
                                            size="small"
                                        />
                                        <Text type="b200" color="secondary">Units</Text>
                                    </Row>
                                    )}
                                </Column>
                            </Row>
                            {processPathOverride.wipOverride !== undefined && processPathOverride.wipOverride !== null && (
                                <OverrideReasons 
                                    processPathName={processPathName}
                                    processPathOverride={processPathOverride}
                                    updateProcessPathOverride={updateProcessPathOverride}
                                    overrideReasonsList={wipOverrideReasonsList}
                                    readOnly={readOnly}
                                    overrideType="wip"
                                    overrideValue={processPathOverride.wipOverride}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProcessPathVolumeRow;